import { IndexedDBService } from '../../indexed-db/services/db_service';
import { VoyageDialogPopupManager } from '../../local_modules/voyage-dialog-popup/src/voyage-dialog-popup-manager';
import { TerminalsModel } from '../models/terminals-model';
import { ScheduleEntry, SimplifiedPortCall } from '../models/types/voyage-schedules-model.types';
import { VoyagePlan } from '../models/vessel-planning-model';
import { FeatureOrigin, RouteGeometry, VoyageRoute, VoyageRoutePointFeature } from "../models/voyage-routing-model";

export const ONE_DAY = 1000 * 60 * 60 * 24
export const ONE_MONTH = ONE_DAY * 31
export const THREE_MONTHS = ONE_MONTH * 3
export const SIX_MONTHS = ONE_MONTH * 6
export const TWELVE_MONTHS = ONE_MONTH * 12
const TOLERANCE = 1e-5;

export enum DeviationType{
    Default = 'default',
    Deviation = 'deviation',
    LowDeviation = 'lowdeviation',
    IsAligned = 'aligned',
}

export function formatToTitleCase(text: string): string {
    return text.split(" ")
    .map((word) => {
        return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    })
    .join(" ");
}

export function debounce(callback: Function, wait?: number, context?: any) {
    let timeout: number;
    return (...args: any[]) => {
        // @ts-ignore (implicit any for this)
        context = context ?? this;
        clearTimeout(timeout);
        timeout = window.setTimeout(() => callback.apply(context, args), wait);
    };
}

// Simple delayfunction to wait for various events, e.g. a debounced input
// mostly used with testing
export const delay = (ms: number) => new Promise(res => setTimeout(res, ms))


export function ScrollTo(element: HTMLElement, to: number, duration: number) {
    let start = element.scrollTop,
        change = to - start,
        currentTime = 0,
        startTime = new Date().getTime();

    let animateScroll = function(){
        currentTime = Math.min(new Date().getTime() - startTime, duration);

        const val = function(currentTime, start, change, duration) {
            currentTime /= duration/2;
            if (currentTime < 1) return change/2*currentTime*currentTime + start;
            currentTime--;
            return -change/2 * (currentTime*(currentTime-2) - 1) + start;
        }(currentTime, start, change, duration);
        element.scrollTop = val;
        if(currentTime < duration) window.requestAnimationFrame ? window.requestAnimationFrame(animateScroll) : window.setTimeout(animateScroll, 24);
    };

    animateScroll();
}

export function GetFormattedDate(date: string|number|Date) {
    const dateVal = new Date(date);
    const month = dateVal.toLocaleString('en-us', { month:'short', timeZone: 'UTC' });
    const timeOfDay = `${dateVal.getUTCHours().toString().padStart(2,'0')}:${dateVal.getUTCMinutes().toString().padStart(2,'0')}`;
    return `${dateVal.getUTCDate()} ${month} ${dateVal.getUTCFullYear()} ${timeOfDay}`;
}

export function GetFormattedTimeWithDate(date: string|number|Date) {
    const dateVal = new Date(date);
    const month = dateVal.toLocaleString('en-us', { month:'short', timeZone: 'UTC' });
    const timeOfDay = `${dateVal.getUTCHours().toString().padStart(2,'0')}:${dateVal.getUTCMinutes().toString().padStart(2,'0')}`;
    return `${timeOfDay} ${dateVal.getUTCDate()} ${month} ${dateVal.getUTCFullYear()}`;
}

export function GetFormattedDateNoYear(date: string|number|Date) {
    const dateVal = new Date(date);
    const month = dateVal.toLocaleString('en-us', { month: 'short', timeZone: 'UTC' });
    const timeOfDay = `${dateVal.getUTCHours().toString().padStart(2,'0')}:${dateVal.getUTCMinutes().toString().padStart(2,'0')}`;
    return `${month} ${dateVal.getUTCDate()} ${timeOfDay}`;
}

export function GetFormattedDayMonth(date: string|number|Date) {
    const dateVal = new Date(date);
    const month = dateVal.toLocaleString('en-us', { month: 'short', timeZone: 'UTC' });
    return `${month} ${dateVal.getUTCDate()}`;
}

export function GetUTCTimestamp(date: string|number|Date): string {
    const dateVal = new Date(date);
    return `${dateVal.getUTCHours().toString().padStart(2,'0')}:${dateVal.getUTCMinutes().toString().padStart(2,'0')}`;
}

export function ConvertDateToAnotherTimeZone(date: string, timezone: string) {
    const dateString = new Date(date).toLocaleString('en-US', {
        timeZone: timezone
    });

    const dateLocal = new Date(dateString);
    const dateUTC = Date.UTC(dateLocal.getFullYear(), 
                             dateLocal.getMonth(), 
                             dateLocal.getDate(), 
                             dateLocal.getHours(), 
                             dateLocal.getMinutes(), 
                             dateLocal.getSeconds());

    return dateUTC;
}

export function FormatDateToLocalTime(date: string | number, timezone: string) {
    const convertedDate = new Date(date);
    
    const dateString = convertedDate.toLocaleString('en-US', {timeZone: timezone})
    const month = convertedDate.toLocaleString('en-us', { month: 'short' }); 
  
    const dateLocal = new Date(dateString);
  
    const dateUTC = Date.UTC(dateLocal.getFullYear(), 
                             dateLocal.getMonth(), 
                             dateLocal.getDate(), 
                             dateLocal.getHours(), 
                             dateLocal.getMinutes(), 
                             dateLocal.getSeconds());
  
    const diff = (dateUTC - new Date(date).getTime()) / (1000 * 60 * 60);
  
    const timeOfDay = `${dateLocal.getHours().toString().padStart(2,'0')}:${dateLocal.getUTCMinutes().toString().padStart(2,'0')}`;
    return `${dateLocal.getUTCDate()} ${month} ${timeOfDay} LT (UTC ${diff > 0 ? "+" + diff : diff})`;
  }
  

export function GetFormattedLocalDate(dateStr: string = '') {
    // Input to this function is a date string with a time zone, e.g. 2019-07-01T12:00:00.000+01:00
    // We trim off the time zone information and rewrite it as UTC to prevent GetFormattedDate from converting it
    return GetFormattedDate(dateStr.substring(0, 19) + 'Z');
}

export function smartAge(ageInMilliseconds: number) {
    // Start with hours
    let duration = Math.floor(ageInMilliseconds / (60 * 60 * 1000));
    let label = (duration === 1 ? 'hour' : 'hours');

    // If above 24, convert to days
    if (duration >= 24) {
        duration = Math.floor(duration / 24);
        label = (duration === 1 ? 'day' : 'days');
    }

    return `${duration} ${label}`;
}

export function UUID() {
    // See https://stackoverflow.com/questions/105034/create-guid-uuid-in-javascript
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
        var r = Math.random() * 16 | 0, v = c == 'x' ? r : (r & 0x3 | 0x8);
        return v.toString(16);
      });
}

export function hasApprovedRouteChanged(route1: VoyageRoute, route2: VoyageRoute): boolean {
    // Exclude passagePlan and compare
    const { passagePlan: _, ...strippedRoute1 } = route1;
    const { passagePlan: __, ...strippedRoute2 } = route2;

    return !deepEqual(strippedRoute1, strippedRoute2);
}

export function hasPilotEtaChanged(a: number, b: number): boolean {
    // check if the pilot eta has the difference of more than 30 mins
    if (!a || !b) {
        return false;
    }
    const aDate = new Date(a);
    const bDate = new Date(b);
    const diff = Math.abs(aDate.getTime() - bDate.getTime());
    const diffInMinutes = Math.floor(diff / (1000 * 60));
    if (diffInMinutes < 30) {
        return false;
    }
    return a !== b;
}

export function shouldSendPlantoShore(a: VoyagePlan, b: VoyagePlan): boolean {
    // Destructure without incorrect array assignment
    const { routes: oldRoutes, passagePlan: _, id: __, create_dtm: ___, update_dtm: ____, currentSelectedRouteId: _________, ...oldStrippedPlan } = a;
    const { routes: newRoutes, passagePlan: _____, id: ______, create_dtm: _______, update_dtm: ________,currentSelectedRouteId: __________, ...newStrippedPlan } = b;

    const oldApprovedRoute = oldRoutes?.[a.selectedRouteIndex];
    const newApprovedRoute = newRoutes?.[b.selectedRouteIndex];

    const approvedRouteChanged = oldApprovedRoute.recalcNeeded && !newApprovedRoute.recalcNeeded;

    const planChanged = !deepEqual(oldStrippedPlan, newStrippedPlan);

    return approvedRouteChanged ?? planChanged;
}


export function deepEqualPlans(a: VoyagePlan, b: VoyagePlan): boolean {
    // Create a copy of the plans
    const planA = { ...a };
    const planB = { ...b };

    // Remove the properties that should not be compared
    delete planA.id;
    delete planA.create_dtm;
    delete planA.update_dtm;

    delete planB.id;
    delete planB.create_dtm;
    delete planB.update_dtm;

    return deepEqual(planA, planB);
}

// Compares a and b
// Note: Assumes plain data inputs (ie. Objects, Arrays and basic types)
export function deepEqual(a: any, b: any): boolean
{
    if (a === b)
    {
        return true;
    }

    if (Number.isNaN(a) && Number.isNaN(b))
    {
        return true;
    }

    if (typeof a === "number" && typeof b === "number") {
        const aRounded = Number(a.toFixed(2));
        const bRounded = Number(b.toFixed(2));
        return aRounded === bRounded;
    }

    if (typeof(a) === "object" && a !== null)
    {
        if (Array.isArray(a))
        {
            if (!Array.isArray(b) || a.length !== b.length)
            {
                return false;
            }
            for (let i = 0; i < a.length; i++)
            {
                if (!deepEqual(a[i], b[i]))
                {
                    return false;
                }
            }
            return true;
        }
        else
        {
            // a is an Object
            if (typeof(b) !== "object" || b === null || Array.isArray(b))
            {
                return false;
            }
            if (Object.keys(a).length !== Object.keys(b).length)
            {
                return false;
            }
            for (const propName in a)
            {
                if (!(propName in b))
                {
                    return false;
                }
                if (!deepEqual(a[propName], b[propName]))
                {
                    return false;
                }
            }
            return true;
        }
    }
    
    return false;
}

// Creates a deep copy of the given object (while keeping type intact)
// Note: Assumes input that can be JSONified (ie. Objects, Arrays and basic types without circular references)
export function deepCopy<T>(a: T): T
{
    return JSON.parse(JSON.stringify(a));
}

// Reverse version of the arrays findIndex() function
export function findLastIndex<T>(array: Array<T>, callback: (element: T, index?: number, array?: Array<T>) => boolean): number
{
    for (let i = array.length-1; i >= 0; i--)
    {
        if (callback(array[i], i, array))
        {
            return i;
        }
    }
    return -1;
}

export function verifyETAOverlapWithDepartureTime(eta: number, scheduleEntry: ScheduleEntry, portCall: SimplifiedPortCall) {
    if (eta >= new Date(scheduleEntry.arrivalPortCall!.current!.departureAt).getTime()) {
        portCall.departureAt = new Date(eta + 60*60*1000).toISOString();

        VoyageDialogPopupManager.createDialog({
            headerText: ETAExeedsThePortCallDepartureTimeTitle,
            bodyText: "Departure time at arrival port has been advanced by 1h",
            acceptButtonText: "Ok",
        });
    }
}

export const ETAExeedsThePortCallDepartureTimeTitle = "ETA exceeds the port call departure time";

/**
 * @description takes a JS Date time stamp and pretty tells the user time lapsed since time.
 * @param timestamp The string that should be made human readable
 * @returns string (example: 5 minutes ago)
 */
export function prettyTimelapsed(timestamp?: string): string {
    if (!timestamp) {
        return '-'
    }

    const currentTs = (new Date()).getTime();
    // Notification and position APIs return UTC timestamps, but some responses omit
    // the timezone suffix. A timezone-less ISO value is otherwise parsed as local time.
    const hasTimezone = /(?:Z|[+-]\d{2}:?\d{2})$/i.test(timestamp);
    const utcTimestamp = hasTimezone ? timestamp : `${timestamp}Z`;
    const secondsLapsed = (currentTs - (new Date(utcTimestamp)).getTime()) / 1000;

    if (secondsLapsed < 60) {
        return "less than 1 min.";
    } else if (secondsLapsed < 60 * 60) {
        return `${(secondsLapsed/60).toFixed(0)} min.`;
    } else if (secondsLapsed < (60 * 60 * 24)) {
        // toFixed is rounding number, so 1.49 will be rounded to 1
        if (secondsLapsed < (60 * 60 * 1.5)) {
            return `${(secondsLapsed/3600).toFixed(0)} hour`;
        }
        return `${(secondsLapsed/3600).toFixed(0)} hours`;
    }

    return `${(secondsLapsed / (60 * 60 * 24)).toFixed(0)} days`;
}


// returns the lastest week/year date from array of week/year strings
export function getLatestWeekDate(dates: string[]) {
    if (!dates || dates.length === 0) {
        return null;
    }
    
    return dates.map(d => d.split('/')).sort((a, b) => a[1].localeCompare(b[1]) || a[0].localeCompare(b[0]))[dates.length -1].join('/');
}

export function getWaypointsFromRoute(routeGeometry: RouteGeometry): VoyageRoutePointFeature[] {
    const copyPoints = JSON.parse(JSON.stringify(routeGeometry.filter(feature => feature.geometry.type === "Point" && feature.properties.type !== "Informational")));

    const legs = routeGeometry.filter(feature => feature.geometry.type === "LineString");

    for (let i = 0; i < legs.length; i++) {
        if(copyPoints[i] && legs[i].properties.leg_type) {
            copyPoints[i].properties.leg_type = legs[i].properties.leg_type;
        }
    }

    for (let i = 0; i < copyPoints.length - 1; i++) {
        const point = copyPoints[i];
        const nextPoint = copyPoints[i + 1];
        if (
            point.properties.is_stoppage_point === true &&
            point.properties.stoppage_end !== true &&
            nextPoint.properties.is_stoppage_point === true &&
            nextPoint.properties.stoppage_end === true &&
            legs[i + 1]?.properties.leg_type
        ) {
            point.properties.leg_type = legs[i + 1].properties.leg_type;
            nextPoint.properties.leg_type = legs[i + 1].properties.leg_type;
        }
    }

    return copyPoints;
}

/**
 * knotsToMps
 * Converts nautical miles per hour (knots) to Meters pr. second.
 * @param knots Nautical miles per hour.
 * @returns number
 */
export function knotsToMps(knots: number) {
    return knots * 1852 / 3600
}
/**
 * mpsToKnots
 * Converts Meters pr. second to nautical miles per hour (knots).
 * @param mps Meters per. second.
 * @returns number
 */
export function mpsToKnots(mps: number) {
    return mps * 3600 / 1852
}

export const passagePlanPropertiesOnRoute = ["description", "reminders", "encCellsUsed", "booksAndTables", "chartsAndLightsNotes", "sailingDirections", "listOfRadioSignals", "listOfLights",
                                             "gpsEcdisRadarMap", "paperCharts", "pubCorrectedToWeek", "tideTables", "publicationsNotes", "mastersOrders", "captain", "navigationalOfficer", "planDate"];

// Remove all special characters that are not compatible for html attributes
export function stringToHTMLAttribute(s: string) {
    return s.replace(/[`~!@#$%^&*()_|+\-=?;:'",.<>\s\{\}\[\]\\\/]/gi, '').toLowerCase();
}

export function isObjectEmpty(obj:{}) {
    return Object.keys(obj).length === 0;
  }
export function GetFormattedDateDayMonthTimeNoYear(date: string | number | Date) {
    const dateVal = new Date(date);
    const month = dateVal.toLocaleString('en-us', { month: 'short', timeZone: 'UTC' });
    const timeOfDay = `${dateVal.getUTCHours().toString().padStart(2, '0')}:${dateVal.getUTCMinutes().toString().padStart(2, '0')}`;
    return ` ${dateVal.getUTCDate()} ${month}  ${timeOfDay}`;
}


function compareCoordinates(coords1: [number, number][], coords2: [number, number][]): boolean {
    if (coords1.length !== coords2.length) return false;
    for (let i = 0; i < coords1.length; i++) {
        if (coords1[i][0] !== coords2[i][0] || coords1[i][1] !== coords2[i][1]) {
            return false;
        }
    }
    return true;
}

function comparePointCoordinates(coord1: [number, number], coord2: [number, number]): boolean {
    return coord1[0] === coord2[0] && coord1[1] === coord2[1];
}

function compareGeometries(geometry1: any, geometry2: any): boolean {
    if (geometry1.type !== geometry2.type) return false;

    if (geometry1.type === 'LineString') {
        return compareCoordinates(geometry1.coordinates, geometry2.coordinates);
    } else if (geometry1.type === 'Point') {
        return comparePointCoordinates(geometry1.coordinates, geometry2.coordinates);
    }

    return false;
}

export function compareRoutes(bprRoute: VoyageRoute, newRoute: VoyageRoute): boolean {
    const [,bprPilotRouteGeometry,] = splitRouteGeometry(bprRoute);
    const [,newPilotRouteGeometry,] = splitRouteGeometry(newRoute);

    for (let i = 0; i < bprPilotRouteGeometry.length; i++) {
        if (bprPilotRouteGeometry[i].geometry.type === 'Point' && bprPilotRouteGeometry[i].properties.type === 'Hard') {
            if (!compareGeometries(bprPilotRouteGeometry[i].geometry, newPilotRouteGeometry[i].geometry)) {
                    if(newPilotRouteGeometry[i].properties.origin === FeatureOrigin.Zone) {
                        continue;
                    }
                    else {
                        return false;
                    }
                }
            } 
            if (bprPilotRouteGeometry[i].properties.leg_type !== newPilotRouteGeometry[i].properties.leg_type) {
                return false
            }
        }
        return true;
}

export function splitRouteGeometry(route: VoyageRoute): [RouteGeometry, RouteGeometry, RouteGeometry] {
    let startIndex = -1;
    let endIndex = route.route_geometry.length;

    // Find pilot points
    var pilotPointIndeces: number[] = [];

    for (const [index, feature] of route.route_geometry.entries()) {
        if (feature.geometry.type == "Point" ) {
            if (feature.properties["pilotPoint"]) {
                pilotPointIndeces.push(index);
            }
        }
    }

    // Note: One pilot point means it is both arrival and departure pilot point (ie. there is no pilot -> pilot)
    if (pilotPointIndeces.length >= 1) {
        startIndex = pilotPointIndeces[0];
        endIndex = pilotPointIndeces[pilotPointIndeces.length-1];
    }

    const splitRoute: [RouteGeometry, RouteGeometry, RouteGeometry]= [[],[],[]];
    for (const [index, feature] of route.route_geometry.entries()) {
        if (index <= startIndex) {
            // Berth -> pilot
            splitRoute[0].push(feature);
        }
        if (index >= startIndex && index <= endIndex && startIndex != endIndex && (feature.properties.type === "Hard" || feature.properties.type === "Weather")) {
            // Pilot -> pilot
            splitRoute[1].push(feature);
        }
        if (index >= endIndex) {
            // Pilot -> berth
            splitRoute[2].push(feature);
        }
    }

    return splitRoute;
}

function getFormattedTime(date: Date) {
    return `${date.getUTCHours().toString().padStart(2, '0')}:${date.getUTCMinutes().toString().padStart(2, '0')}`;
}

function getFormattedDate(date: Date) {
    return date.getUTCDate().toString().padStart(2, '0');
}

function getFormattedMonth(date: Date) {
    return date.toLocaleString('en-us', { month: 'short', timeZone: 'UTC' });
}

export function getFormattedDateUTC(date = new Date()) {
    const timePart = getFormattedTime(date);
    const month = getFormattedMonth(date);
    const datePart = getFormattedDate(date);
    return `${datePart} ${month} ${timePart} UTC`;
}

export async function formatTooltipTime(time: string | null, locationId?: string){
    if (!time) { return ""; }

    let timeLocal = new Date(time).toISOString();
    if (locationId && !TerminalsModel!.isPseudoTerminal(locationId))
    {
        timeLocal = await TerminalsModel!.getTerminalLocaltime(locationId, time);
    }
    timeLocal = timeLocal.substring(0,16);
    const d = new Date(timeLocal);
    const day   = String(d.getDate()).padStart(2, "0");
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const year  = d.getFullYear();

    let hours = d.getHours();              
    const minutes = String(d.getMinutes()).padStart(2, "0");            
    const hoursStr = String(hours).padStart(2, "0");

    return `${day}/${month}/${year} ${hoursStr}:${minutes}`;
}

export function getDeviationClass(scheduledTime: number | string | null , communicatedTime: number | string | null){
    if (!scheduledTime || !communicatedTime || communicatedTime == -1) return DeviationType.Default;

    const scheduledDate =  new Date(scheduledTime);
    const communicatedDate = new Date(communicatedTime);
    const timeDiffInHours = Math.abs(scheduledDate.getTime() - communicatedDate.getTime()) / (1000 * 60 * 60);

    /*Colour Alignment for deviation
      More than 120 minutes of deviation == RED Colour
      Between 30 minutes to 120 minutes of deviation == YELLOW Colour
      Between 0 to 30 minutes of deviation == GREEN Colour
    */
   
    if (timeDiffInHours > 2)
        return DeviationType.Deviation;
    else if (timeDiffInHours > 0.5)
        return DeviationType.LowDeviation;
    else if (timeDiffInHours <= 0.5)
        return DeviationType.IsAligned

    return DeviationType.Default;
}

export const tooltipText = {
    actual: "This data source is from Scheduling and 'Act' – indicates it's an Actual value.",
    schedule: "This data source is from Scheduling and 'Sch' – indicates it's a Scheduled value.",
    hasDeviation: "This data source is from Scheduling and there is some time gap between the entered value vs. received time from Scheduling.",
    default: "This data source is from Scheduling",
    declined: "This data source is from Scheduling and 'Sch' – indicates it's a Scheduled value and ETA changes has been declined."
  };

export async function mapOnlineStatusData () {
    const vesselStatusData = await IndexedDBService.vesselStatusActual.get();
    const latestPosition = await IndexedDBService.position.get();
    const latestPositionData = {
        timestamp: latestPosition?.data?.timestamp,
        latitude: latestPosition?.data?.latitude,
        longitude: latestPosition?.data?.longitude,
        source: latestPosition?.data?.source,
    };

    switch(vesselStatusData[0].status) {
        case 'on-voyage':
            const voy = vesselStatusData[0].voyage;
            return {
                position: latestPositionData,
                schedule: {
                    status: vesselStatusData[0].status,
                    voyage: {
                        id: voy.id,
                        departureTerminalCode: voy.departureTerminalCode,
                        departureTerminalUnLocode: voy.departureTerminalUnLocode,
                        departureTerminalServiceCode: voy.departureTerminalServiceCode,
                        departureAt: voy.departureAt,
                        arrivalTerminalCode: voy.arrivalTerminalCode,
                        arrivalTerminalUnLocode: voy.arrivalTerminalUnLocode,
                        arrivalTerminalServiceCode: voy.arrivalTerminalServiceCode,
                        arrivalAt: voy.arrivalAt,
                    },
                }
            }
        case 'in-port':
            const portCall = vesselStatusData[0].portCall;
            return {
                position: latestPositionData,
                schedule: {
                    status: vesselStatusData[0].status,
                    voyage: {
                        id: portCall.id,
                        departureTerminalCode: portCall.terminalCode,
                        departureTerminalUnLocode: portCall.terminalUnLocode,
                        departureTerminalServiceCode: portCall.serviceCode,
                        departureAt: portCall.departureAt,
                        arrivalTerminalCode: portCall.terminalCode,
                        arrivalTerminalUnLocode: portCall.terminalUnLocode,
                        arrivalTerminalServiceCode: portCall.serviceCode,
                        arrivalAt: portCall.arrivalAt,
                    },
                }
            }
        default:
            return {};
    } 
}

export function slowNetworkDetected() {

    if ('connection' in navigator) {
        const conn = (navigator as any).connection;
        
        // Guard against undefined connection properties
        if (!conn) {
            return false;
        }

        const isSlowConnection = 
            // Check multiple indicators for slow connection
            // 1. Very slow downlink (< 250 kbps = 0.25 Mbps)
            (conn.downlink !== undefined && conn.downlink > 0 && conn.downlink < 0.25) ||
            // 2. Downlink is 0 AND effectiveType indicates slow connection
            (conn.downlink === 0 && (conn.effectiveType === 'slow-2g' || conn.effectiveType === '2g')) ||
            // 3. High latency (> 1.5 seconds)
            (conn.rtt !== undefined && conn.rtt > 1500) ||
            // 4. Data saver mode enabled
            conn.saveData === true;

        if (isSlowConnection) {
            // Only dispatch event if we haven't recently to avoid spam
            const lastDispatchTime = parseInt(sessionStorage.getItem('last-slow-network-event') || '0');
             const now = Date.now();
            if (now - lastDispatchTime > 5000) { // 5 second throttle
                sessionStorage.setItem('last-slow-network-event', now.toString());
                window.dispatchEvent(new CustomEvent('slow-network-detected', {
                    detail: {
                        effectiveType: conn.effectiveType,
                        downlink: conn.downlink,
                        rtt: conn.rtt,
                        saveData: conn.saveData
                    }
                }));
            }
            return true;
        }
        return false;
    } else {
        // Fallback: assume good network if API not available
        return false;
    }
}

const BPR_REASON_WINDOW_AFTER_ETD_MS = 24 * 60 * 60 * 1000;

// isWithinBprReasonWindow reports whether the reason for not choosing a Star Route, and the Star Route
// adoption verdict, are still collected for a voyage: until one day after ETD, and never after ETA.
// Vessels often plan the route, seeing the Star Routes, before departure but approve and send the plan
// shortly after departure.
export function isWithinBprReasonWindow(
    etd: string | number | null | undefined,
    eta: string | number | null | undefined,
): boolean {
    if (!etd || !eta) {
        return false;
    }
    const toTimestamp = (time: string | number) => typeof time === 'string' ? new Date(time).getTime() : time;
    const now = Date.now();
    return now < toTimestamp(etd) + BPR_REASON_WINDOW_AFTER_ETD_MS && now < toTimestamp(eta);
}

export function isOnline(): boolean {
    const isSlowNetwork = slowNetworkDetected();
    if(isSlowNetwork && (localStorage.getItem('isSlowNetworkOfflineMode') === 'Yes')) {
       return false;
    } 
    // Allow DevTools override via localStorage
    if (localStorage.getItem('forceOffline') === 'true') return false;

    if((navigator as any).onLine) {
        return true;
    }
    return false;
}

// Env feature toggle (appenv.featureToggles.starRouteDeviation) for the star-route waypoint
// deviation visualisation. Off by default so the feature can ship dark.
export function isStarRouteDeviationEnabled(): boolean {
    return (<any>window)?.["appenv"]?.featureToggles?.starRouteDeviation === true;
}

export function isAdoptionIndicatorScheduleCardEnabled(): boolean {
    return (<any>window)?.["appenv"]?.featureToggles?.adoptionIndicatorScheduleCard === true;
}
  

  export function getNamesOfRoutesFromPlan(plan: VoyagePlan): string[] {
    if (!plan || !plan.routes || plan.routes.length === 0) {
        return [];
    }
    
    return plan.routes.map(route => route.name ?? '');
}

export function getEdittedStarRouteName(existingNames: string[], newName: string): string {
    // If the name doesn't exist, return it as is
    if (!existingNames.includes(newName)) {
      return newName;
    }

    // Extract base name and existing number if present
    const match = newName.match(/^(.+?)\s*\((\d+)\)$/);
    const baseName = match ? match[1].trim() : newName;

    // Find all existing numbers for this base name
    const existingNumbers = new Set<number>();

    for (const name of existingNames) {
      if (name === baseName) {
        // Base name without number counts as "0" or we start from 1
        continue;
      }

      const nameMatch = name.match(/^(.+?)\s*\((\d+)\)$/);
      if (nameMatch && nameMatch[1].trim() === baseName) {
        existingNumbers.add(parseInt(nameMatch[2]));
      }
    }

    // Find the next available number starting from 1
    let counter = 1;
    while (existingNumbers.has(counter)) {
      counter++;
    }

    return `${baseName} (${counter})`;
  }

/**
 * Rounds a number to a specified number of decimal digits
 * @param value The number to round
 * @param digits The number of decimal places to round to (default: 2)
 * @returns The rounded number
 */
export function roundToDigits(value: number, digits: number = 2): number {
     if (value === null || value === undefined || Number.isNaN(value)) {
            return value;
        }

            return Number(value.toFixed(digits));
    }
